/**
 * Base API Client
 * Universal HTTP client for all API calls across the application
 * Features: interceptors, error handling, retry logic, timeout, cancellation
 */

import { clearAuthState, getAccessToken, getRefreshToken, setTokens } from "utils/auth";
import { getSelectedLanguage, normalizeLanguageCode } from "utils/language";
import { API_BASE_URL } from "config/api";

class ApiClient {
  constructor(baseURL, config = {}) {
    this.baseURL = baseURL;
    this.config = {
      timeout: 60000, // 60 seconds default to accommodate longer report actions
      retryAttempts: 3,
      retryDelay: 1000,
      ...config,
    };
    this.requestInterceptors = [];
    this.responseInterceptors = [];
  }

  /**
   * Add request interceptor
   * @param {Function} interceptor - Function to modify request config
   */
  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   * @param {Function} onSuccess - Success handler
   * @param {Function} onError - Error handler
   */
  addResponseInterceptor(onSuccess, onError) {
    this.responseInterceptors.push({ onSuccess, onError });
  }

  /**
   * Apply request interceptors
   */
  async applyRequestInterceptors(config) {
    let modifiedConfig = { ...config };
    for (const interceptor of this.requestInterceptors) {
      modifiedConfig = await interceptor(modifiedConfig);
    }
    return modifiedConfig;
  }

  /**
   * Apply response interceptors
   */
  async applyResponseInterceptors(response) {
    let modifiedResponse = response;
    for (const { onSuccess } of this.responseInterceptors) {
      if (onSuccess) {
        modifiedResponse = await onSuccess(modifiedResponse);
      }
    }
    return modifiedResponse;
  }

  /**
   * Handle response errors through interceptors
   */
  async applyErrorInterceptors(error) {
    let modifiedError = error;
    for (const { onError } of this.responseInterceptors) {
      if (onError) {
        modifiedError = await onError(modifiedError);
      }
    }
    return modifiedError;
  }

  /**
   * Create AbortController for request cancellation
   */
  createAbortController() {
    return new AbortController();
  }

  /**
   * Build full URL from endpoint
   */
  buildURL(endpoint) {
    return `${this.baseURL}${endpoint}`;
  }

  /**
   * Core request method with retry logic
   */
  async request(endpoint, options = {}, attempt = 1) {
    try {
      // Apply request interceptors
      const modifiedOptions = await this.applyRequestInterceptors(options);

      // Detect multipart bodies so we don't force JSON headers
      const isFormDataBody = modifiedOptions?.body instanceof FormData;

      // Create abort controller for timeout
      const controller = this.createAbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      // Merge options
      const fetchOptions = {
        ...modifiedOptions,
        signal: modifiedOptions.signal || controller.signal,
        headers: {
          ...(isFormDataBody ? {} : { 'Content-Type': 'application/json' }),
          ...modifiedOptions.headers,
        },
      };

      // Make request
      const response = await fetch(this.buildURL(endpoint), fetchOptions);

      // Clear timeout
      clearTimeout(timeoutId);

      // Check for HTTP errors
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        error.response = response;
        error.requestConfig = { endpoint, options: modifiedOptions };

        // Try to parse error body
        try {
          error.data = await response.json();
        } catch (e) {
          error.data = null;
        }

        throw error;
      }

      // Parse response
      const data = await response.json();

      // Apply response interceptors
      const modifiedResponse = await this.applyResponseInterceptors({
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });

      return modifiedResponse.data;

    } catch (error) {
      // Check if we should retry
      const shouldRetry =
        attempt < this.config.retryAttempts &&
        error.name !== 'AbortError' &&
        (!error.status || error.status >= 500);

      if (shouldRetry) {
        // Wait before retry
        await new Promise(resolve =>
          setTimeout(resolve, this.config.retryDelay * attempt)
        );

        // Retry
        return this.request(endpoint, options, attempt + 1);
      }

      // Apply error interceptors
      const modifiedError = await this.applyErrorInterceptors(error);
      throw modifiedError;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, config = {}) {
    return this.request(endpoint, {
      method: 'GET',
      ...config,
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data, config = {}) {
    const isFormData = data instanceof FormData;
    return this.request(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      ...config,
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data, config = {}) {
    const isFormData = data instanceof FormData;
    return this.request(endpoint, {
      method: 'PUT',
      body: isFormData ? data : JSON.stringify(data),
      ...config,
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data, config = {}) {
    const isFormData = data instanceof FormData;
    return this.request(endpoint, {
      method: 'PATCH',
      body: isFormData ? data : JSON.stringify(data),
      ...config,
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, config = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...config,
    });
  }
}

// Create default instance
const apiClient = new ApiClient(API_BASE_URL);

const isAuthEndpoint = (endpoint = "") => {
  const normalized = String(endpoint || "").replace(/^\/+/, "");
  return /^auth\/(login|refresh|register|logout)\/?$/.test(normalized);
};

const redirectToLogin = (endpoint = "") => {
  if (typeof window === "undefined") return;
  if (isAuthEndpoint(endpoint)) return;
  clearAuthState();
  const path = window.location?.pathname || "";
  if (path !== "/login") {
    window.location.replace("/login");
  }
};

// Attach bearer token from local storage to every request unless already provided
apiClient.addRequestInterceptor((config) => {
  const token = getAccessToken();
  const selectedLanguage = normalizeLanguageCode(getSelectedLanguage());
  config.headers = {
    ...config.headers,
    ...(token && !(config.headers && config.headers.Authorization)
      ? { Authorization: `Bearer ${token}` }
      : {}),
    ...(config.headers && config.headers["Accept-Language"]
      ? {}
      : { "Accept-Language": selectedLanguage }),
  };
  return config;
});

// Add default request interceptor for logging and debug token (development only)
if (process.env.NODE_ENV === 'development') {
  apiClient.addRequestInterceptor((config) => {
    console.log('[API Request]', config);

    // Add debug token for development if available
    if (process.env.REACT_APP_DEBUG_TOKEN) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${process.env.REACT_APP_DEBUG_TOKEN}`,
      };
      console.log('[API Debug] Using debug token for authentication');
    }

    return config;
  });
}

// Add default response interceptor for logging (development only)
if (process.env.NODE_ENV === 'development') {
  apiClient.addResponseInterceptor(
    (response) => {
      console.log('[API Response]', response);
      return response;
    },
    (error) => {
      // Only log non-auth errors to reduce console noise
      // 401 errors are expected when user is not logged in
      if (error.status !== 401) {
        console.error('[API Error]', error);
      } else {
        console.log('[API Auth Required]', error.message);
      }
      return error;
    }
  );
}

// Refresh token interceptor on 401
apiClient.addResponseInterceptor(
  null,
  async (error) => {
    if (error?.status !== 401 || error?._retry) return error;
    const endpoint = error?.requestConfig?.endpoint || "";

    const refresh = getRefreshToken();
    if (!refresh) {
      redirectToLogin(endpoint);
      return error;
    }

    try {
      const refreshRes = await fetch(`${apiClient.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });

      if (!refreshRes.ok) {
        redirectToLogin(endpoint);
        return error;
      }

      const j = await refreshRes.json();
      const newAccess = j?.access_token;
      const newRefresh = j?.refresh_token || refresh;

      if (!newAccess) {
        redirectToLogin(endpoint);
        return error;
      }

      setTokens(newAccess, newRefresh);

      const cfg = error.requestConfig;
      if (!cfg) return error;

      const retryOptions = {
        ...cfg.options,
        headers: {
          ...(cfg.options?.headers || {}),
          Authorization: `Bearer ${newAccess}`,
        },
      };
      error._retry = true;
      return apiClient.request(cfg.endpoint, retryOptions);
    } catch (e) {
      redirectToLogin(endpoint);
      return error;
    }
  }
);

export default apiClient;
export { ApiClient };
